// Shared color constants - the CONFIRMED-SAFE subset only (see project_master_checklist.md,
// COLORSWEEP-SURVEY + COLORSAFE entries).
//
// Scope discipline, deliberately narrow:
//   * These constants NAME values that already exist. Not one hex value changes, so appearance
//     is bit-identical before and after adoption.
//   * The two navies are named SEPARATELY, not unified. The survey found the split is a coherent
//     per-file convention (forms vs shells), not copy-paste drift - unifying them would visibly
//     change 54-68 call sites.
//   * The three greens and three reds are deliberately NOT included. Their meaning is
//     file-dependent (verified by reading every call site): e.g. #1a7a45 is "PAD Buy direction"
//     in ComplianceModule but "success message text" in the forms; #A63A3A is "overdue" in
//     App.js/EmployeeView but "leaver/destructive" in EmployeeProfile. A single global semantic
//     name would be wrong in most places it appeared, so they keep their local literals until a
//     per-context naming decision is made.

/** Brand gold. Single canonical casing - the codebase had #C9A84C (47x) and #c9a84c (68x),
 *  which are the same color, differing only in case. NOTE: #c8a84b (WorkflowDiagram/WorkflowList)
 *  is a genuinely DIFFERENT color and is intentionally not covered by this constant. */
export const GOLD = '#c9a84c';

/** Muted secondary text / icon color, used in both light and dark contexts. */
export const TEXT_MUTED = '#9ba8c0';

/** Primary text color on dark-mode surfaces. */
export const TEXT_DARK_MODE = '#e8e4d8';

/** Navy used by the FORM surfaces (NewStarterForm, LeaverForm, SponsorForm, ComplianceModule,
 *  Login). Distinct from NAVY_SHELL by existing convention - see module note above. */
export const NAVY_FORM = '#1a1a2e';

/** Navy used by the SHELL/profile surfaces (App shells, EmployeeView, EmployeeProfile).
 *  Distinct from NAVY_FORM by existing convention - see module note above. */
export const NAVY_SHELL = '#1a2744';

// ── Semantic tokens (REDESIGN Stage 0) ──────────────────────────────────────
// Added by the full visual redesign pass. Everything above this line is unchanged.
//
// The module note above deliberately deferred the greens and reds because their meaning is
// file-dependent. That decision STILL HOLDS and is not being reversed here: the names below are
// for the GENERIC semantic role only ("this failed", "this succeeded", "this needs attention").
// Where a literal carries a domain meaning rather than a status - #1a7a45 as "PAD Buy direction",
// #A63A3A as "leaver/destructive" in EmployeeProfile - it keeps its local literal. Adopting these
// names there would be wrong, not tidy.
//
// Each value below is the existing MAJORITY value for its role, verified by counting call sites,
// so adopting a token is a no-op at the majority of sites and converges the minority onto a value
// already used elsewhere in the app.

/** Negative/error/destructive. Existing majority value (60 call sites vs 36/31/1 for the
 *  context-specific #A63A3A/#c0392b/#991B1B, which keep their local literals). */
export const DANGER = '#e74c3c';
/** Pale red background for error banners - the forms' existing recipe. */
export const DANGER_BG = '#fde8e8';

/** Positive/complete/approved. Existing majority value (35 call sites). Note #2D4A3E (34) is
 *  WorkflowDiagram's "done node" fill - a dark surface color, not this text/accent role. */
export const SUCCESS = '#27ae60';

/** "Needs attention" amber. The audit found 5 near-identical recipes for this one role
 *  (1.2); these three tokens replace all of them. Values are the most-used of each part. */
export const WARNING = '#f39c12';
export const WARNING_BG = '#fff8e1';
export const WARNING_BORDER = '#f39c12';
export const WARNING_TEXT = '#856404';

/** Card/panel surface. */
export const SURFACE = '#ffffff';
export const SURFACE_DARK = NAVY_SHELL;
/** Page background behind cards. */
export const PAGE_BG = '#f8f9fa';
export const PAGE_BG_DARK = '#0f1a2e';

/** Hairline borders. The 0.5px + these values is Dashboard.js's existing card convention. */
export const BORDER = '#eeeeee';
export const BORDER_DARK = 'rgba(201,168,76,0.15)';
/** Slightly stronger border, for inputs and grouped controls. */
export const BORDER_INPUT = '#dddddd';
export const BORDER_INPUT_DARK = 'rgba(201,168,76,0.2)';

/** Default body text on light surfaces. */
export const TEXT_PRIMARY = '#333333';
/** Headings on light surfaces. */
export const TEXT_HEADING = NAVY_FORM;
/** Faint text - placeholders, empty states. Distinct from TEXT_MUTED, which is the dark-mode
 *  secondary; this is its light-mode counterpart.
 *
 *  A11YCONTRAST (audit A3b): was #aaaaaa, which is 2.32:1 on white and fails WCAG 1.4.3 AA for
 *  normal text. Raised to N_500 (#667085), MEASURED at 4.97:1 on white. N_500 is an EXISTING
 *  token from the approved REDESIGN ramp, not a new value, so this converges onto the palette
 *  rather than adding to it. The brief named #667085 and the ramp already holds exactly that
 *  value, so the two agree.
 *
 *  Declared as a literal rather than `= N_500` because N_500 is defined further down this file
 *  and a const cannot be referenced before its initialiser runs. The value is asserted equal to
 *  N_500 by the A11YCONTRAST test, so the two cannot drift apart silently. */
export const TEXT_FAINT = '#667085';

/** Focus ring. Gold at 45% - visible on both light and dark surfaces, and matches the
 *  existing gold "active/current" convention rather than introducing a new focus color.
 *  Closes the WCAG 2.4.7 failure from `outline: 'none'` with no replacement (audit 4.2). */
/** REGOLD point 2d (2026-10-16): the gold accent is restored everywhere EXCEPT here, and this is
 *  the one colour the correction asks me to judge. MEASURED, not assumed: the original gold ring
 *  at 45% opacity gives 1.42:1 against a white card, 2.40:1 on the navy bar and 2.55:1 on the dark
 *  page. WCAG 2.2 requires 3:1 for a non-text focus indicator, so the gold ring FAILED on every
 *  ground, worst of all on the white cards where most focusable controls sit.
 *
 *  SO IT STAYS NON-GOLD, and it is also no longer the 55% blue the removal build used: that
 *  measured 2.02:1 on white, better than the gold but still short of 3:1. At FULL opacity the same
 *  blue gives 3.87:1 on white, 3.83:1 on navy and 4.87:1 on dark - it passes on all three.
 *
 *  THIS IS THE ONLY GOLD VALUE NOT RESTORED. It is an accessibility indicator rather than brand
 *  decoration, and a focus ring nobody can see is not a style choice. */
export const FOCUS_RING = 'rgba(47,128,237,1)';

// ── REDESIGN Stage 1: the approved token set ────────────────────────────────
// Exact values from ENKO_DESIGN_REVIEW.html, which Marie-Lynne reviewed and approved. That file
// is the source of truth; nothing here is invented. Everything above this line is untouched, so
// the 10 files already importing GOLD/NAVY_FORM/etc. are completely unaffected.
//
// The point of the ramp: the design review measured 145 distinct hex values in the app, of which
// 12 were near-identical greys doing one job. These 10 neutrals replace them. The bias is
// deliberately cool (hue ~220) so the ramp sits under the navy rather than fighting it, which is
// what a pure grey does next to a blue-black.

/** Navy scale. NAVY_700 is the existing brand navy (same value as NAVY_SHELL), kept as the
 *  anchor; the review's verdict was that this is the one genuinely strong asset in the current
 *  palette, so the new system is built around it rather than replacing it. */
export const NAVY_900 = '#0f1729';
export const NAVY_800 = '#152238';
export const NAVY_700 = '#1a2744';
export const NAVY_600 = '#24365c';
export const NAVY_500 = '#33487a';

/** Gold scale. Demoted from four competing jobs (brand + active nav + badge + Login's primary
 *  button) to exactly one: the active/current accent. It never fills a button again. */
export const GOLD_600 = '#a8862f';
export const GOLD_500 = '#c9a84c';
export const GOLD_100 = '#f7f0dc';

/** Cool-biased neutral ramp, 10 steps. */
export const N_0   = '#ffffff';
export const N_25  = '#fcfcfd';
export const N_50  = '#f7f8fa';
export const N_100 = '#eff1f5';
export const N_200 = '#e4e7ec';
export const N_300 = '#d0d5dd';
export const N_400 = '#98a2b3';
export const N_500 = '#667085';
export const N_600 = '#475467';
export const N_700 = '#344054';
export const N_800 = '#1d2939';
export const N_900 = '#101828';

/** Semantic scales, fully separate from the brand hues so "urgent" never reads as "branded". */
export const OK_600   = '#067647';
export const OK_500   = '#17b26a';
export const OK_50    = '#ecfdf3';
export const WARN_600 = '#b54708';
export const WARN_500 = '#f79009';
export const WARN_50  = '#fffaeb';
export const CRIT_600 = '#b42318';
export const CRIT_500 = '#f04438';
export const CRIT_50  = '#fef3f2';
export const INFO_600 = '#175cd3';
export const INFO_50  = '#eff8ff';
